import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  PackageOpen,
  ShoppingBag,
  Truck,
  CheckCircle2,
  Clock,
  MapPin,
  Calendar,
  Loader2,
  CreditCard,
  ShieldCheck,
  Star,
} from 'lucide-react'
import { toast } from 'sonner'
import { Textarea } from '@/components/ui/textarea'
import { useMyOrders } from '@/modules/cart/hooks/use-cart'
import { useMyReviews, useSubmitPartReview } from '@/modules/reviews/hooks/use-reviews'
import { appRoutes } from '@/constants'
import { SectionHeading } from '@/components/common'
import { TrackingDialog } from '@/modules/shipping/components/tracking-dialog'

export function CustomerOrdersPage() {
  const { data: orders = [], isLoading } = useMyOrders()
  const { data: myReviews = [] } = useMyReviews()
  const submitPartReview = useSubmitPartReview()
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null)
  const [reviewingOrder, setReviewingOrder] = useState<any | null>(null)
  const [trackingOrder, setTrackingOrder] = useState<any | null>(null)
  const [rating, setRating] = useState<number>(5)
  const [hoverRating, setHoverRating] = useState<number>(0)
  const [comment, setComment] = useState<string>('')

  const getOrderReview = (orderId: number) =>
    myReviews.find((r: any) => r.reviewType === 'PART' && Number(r.referenceId) === Number(orderId))

  const getStatusBadge = (status?: string) => {
    const s = (status || '').toUpperCase()
    switch (s) {
      case 'DELIVERED':
        return (
          <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-bold">
            <CheckCircle2 className="size-3 mr-1" /> Delivered
          </Badge>
        )
      case 'SHIPPED':
        return (
          <Badge className="bg-blue-500/10 text-blue-500 border border-blue-500/20 font-bold">
            <Truck className="size-3 mr-1" /> Shipped & In Transit
          </Badge>
        )
      case 'CANCELLED':
        return (
          <Badge className="bg-red-500/10 text-red-500 border border-red-500/20 font-bold">
            Cancelled
          </Badge>
        )
      case 'CONFIRMED':
        return (
          <Badge className="bg-purple-500/10 text-purple-500 border border-purple-500/20 font-bold">
            <CheckCircle2 className="size-3 mr-1" /> Confirmed
          </Badge>
        )
      default:
        return (
          <Badge className="bg-amber-500/10 text-amber-500 border border-amber-500/20 font-bold">
            <Clock className="size-3 mr-1" /> Processing Order
          </Badge>
        )
    }
  }

  const formatCurrency = (val: any) => {
    if (val === null || val === undefined) return '₹0'
    const num = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9.]/g, ''))
    if (isNaN(num)) return '₹0'
    return `₹${num.toLocaleString('en-IN')}`
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeading
          eyebrow="Purchases & Order History"
          title="My Spare Parts Orders"
          description="Track order dispatch status, view package tracking numbers, delivery addresses, and invoice items."
          align="left"
        />

        <Button asChild variant="outline" className="shrink-0 font-bold">
          <Link to={appRoutes.parts}>
            <ShoppingBag className="mr-2 size-4" /> Browse Parts Catalog
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="p-20 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading your parts order history...</p>
        </div>
      ) : orders.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-border flex flex-col items-center justify-center space-y-4">
          <div className="size-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <PackageOpen className="size-8" />
          </div>
          <div className="space-y-1">
            <h3 className="font-display text-lg font-bold text-foreground">No Orders Placed Yet</h3>
            <p className="text-xs text-muted-foreground max-w-sm">
              You haven't ordered any vehicle spare parts or tuning components yet. Explore our verified OEM and aftermarket catalog.
            </p>
          </div>
          <Button asChild className="bg-primary text-white hover:bg-primary/90 glow-sm font-semibold">
            <Link to={appRoutes.parts}>
              <ShoppingBag className="mr-2 size-4" /> Explore Parts Store
            </Link>
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order: any) => {
            const formattedTotal = formatCurrency(order.totalAmount)
            const orderDate = new Date(order.createdAt).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
            const currentStatus = order.orderStatus || order.status || 'CONFIRMED'
            const orderReview = getOrderReview(order.id)

            return (
              <Card
                key={order.id}
                className="overflow-hidden border border-border bg-card/90 hover:border-primary/40 transition-colors shadow-xs"
              >
                {/* Order Top Bar */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border/80 bg-muted/30 px-6 py-4">
                  <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                    <div>
                      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        Order Number
                      </span>
                      <p className="font-mono text-sm font-bold text-foreground">{order.orderNumber}</p>
                    </div>

                    <div className="hidden sm:block">
                      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        Date Placed
                      </span>
                      <p className="text-xs font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
                        <Calendar className="size-3.5 text-muted-foreground" /> {orderDate}
                      </p>
                    </div>

                    <div>
                      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        Total Amount
                      </span>
                      <p className="font-display text-sm font-extrabold text-foreground">{formattedTotal}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2.5">
                    {getStatusBadge(currentStatus)}

                    {currentStatus === 'DELIVERED' && (
                      orderReview ? (
                        <span className="flex items-center gap-1.5 text-xs font-bold text-amber-500 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1.5 rounded-lg">
                          <Star className="size-3.5 fill-amber-500 text-amber-500" />
                          Rated {orderReview.rating}★
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs gap-1.5 shadow-xs"
                          onClick={() => {
                            setReviewingOrder(order)
                            setRating(5)
                            setComment('')
                          }}
                        >
                          <Star className="size-3.5 fill-white" /> Write Review
                        </Button>
                      )
                    )}

                    {order.awbNumber && (
                      <Button
                        size="sm"
                        className="bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 font-bold text-xs gap-1.5"
                        onClick={() => setTrackingOrder(order)}
                      >
                        <Truck className="size-3.5 text-primary" /> Track Courier
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      className="font-bold text-xs"
                      onClick={() => setSelectedOrder(order)}
                    >
                      View Details
                    </Button>
                  </div>
                </div>

                {/* Order Items Preview */}
                <CardContent className="p-6">
                  <div className="divide-y divide-border/60">
                    {order.items?.map((item: any) => {
                      const itemPrice = typeof item.price === 'number' ? item.price : Number(String(item.price).replace(/[^0-9.]/g, '')) || 0
                      const itemQty = Number(item.quantity) || 1
                      const itemTotal = typeof item.total === 'number'
                        ? item.total
                        : (item.total ? Number(String(item.total).replace(/[^0-9.]/g, '')) : itemPrice * itemQty)

                      return (
                        <div key={item.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                          <div className="space-y-1">
                            <h4 className="font-display text-sm font-bold text-foreground">
                              {item.title}
                            </h4>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono">
                              <span>Part #{item.partNumber}</span>
                              <span>•</span>
                              <span>Qty: {itemQty}</span>
                              <span>•</span>
                              <span>Unit: {formatCurrency(itemPrice)}</span>
                            </div>
                          </div>

                          <div className="text-left sm:text-right font-display text-sm font-extrabold text-foreground">
                            {formatCurrency(itemTotal)}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Shipping Destination Summary */}
                  {order.shippingAddress && (
                    <div className="mt-4 pt-4 border-t border-border/60 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="size-3.5 text-primary shrink-0" />
                        <span>
                          Ship to: <strong className="text-foreground">{order.shippingAddress.fullName}</strong>, {order.shippingAddress.city}, {order.shippingAddress.state} ({order.shippingAddress.postalCode})
                        </span>
                      </div>
                      <span className="font-mono text-[11px] bg-muted/60 border border-border/60 px-2.5 py-1 rounded-md text-foreground flex items-center gap-1.5 font-medium">
                        <CreditCard className="size-3 text-emerald-500" />
                        Payment: {order.paymentMethod || 'Online Verified'}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Order Details Modal */}
      <Dialog open={Boolean(selectedOrder)} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold flex items-center justify-between">
              <span>Order #{selectedOrder?.orderNumber}</span>
              {selectedOrder && getStatusBadge(selectedOrder.orderStatus || selectedOrder.status)}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-5 pt-2 text-sm">
              {/* Delivery Address */}
              <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Truck className="size-3.5 text-primary" /> Delivery Address
                </span>
                <p className="font-bold text-foreground">{selectedOrder.shippingAddress?.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {selectedOrder.shippingAddress?.addressLine1}
                  {selectedOrder.shippingAddress?.addressLine2 ? `, ${selectedOrder.shippingAddress.addressLine2}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} - {selectedOrder.shippingAddress?.postalCode}
                </p>
                <p className="text-xs text-muted-foreground">Contact: {selectedOrder.shippingAddress?.phone}</p>
              </div>

              {/* Bigship Courier Tracking Card */}
              {selectedOrder.awbNumber && (
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-3.5 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                      <Truck className="size-3.5 text-primary" /> Courier: {selectedOrder.courierPartner || 'Delhivery Express Surface'}
                    </span>
                    <p className="text-[11px] font-mono text-muted-foreground">
                      AWB: <strong>{selectedOrder.awbNumber}</strong>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-primary text-white font-bold text-xs gap-1 h-8"
                    onClick={() => {
                      const cur = selectedOrder
                      setSelectedOrder(null)
                      setTrackingOrder(cur)
                    }}
                  >
                    <Truck className="size-3" /> Live Tracking
                  </Button>
                </div>
              )}

              {/* Items Breakdown */}
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Items Purchased ({selectedOrder.items?.length})
                </span>
                <div className="divide-y divide-border rounded-xl border border-border p-3">
                  {selectedOrder.items?.map((item: any) => {
                    const itemPrice = typeof item.price === 'number' ? item.price : Number(String(item.price).replace(/[^0-9.]/g, '')) || 0
                    const itemQty = Number(item.quantity) || 1
                    const itemTotal = typeof item.total === 'number'
                      ? item.total
                      : (item.total ? Number(String(item.total).replace(/[^0-9.]/g, '')) : itemPrice * itemQty)

                    return (
                      <div key={item.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between text-xs">
                        <div>
                          <p className="font-bold text-foreground">{item.title}</p>
                          <p className="text-muted-foreground font-mono">
                            Part #{item.partNumber} • Qty: {itemQty}
                          </p>
                        </div>
                        <span className="font-display font-bold text-foreground">
                          {formatCurrency(itemTotal)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Payment & Total Summary */}
              <div className="rounded-xl border border-border/80 bg-card p-4 space-y-2 text-xs">
                <div className="flex justify-between text-muted-foreground">
                  <span>Payment Method</span>
                  <span className="font-semibold text-foreground">{selectedOrder.paymentMethod || 'Online Card / UPI'}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Payment Status</span>
                  <span className="font-semibold text-emerald-500 font-mono">
                    {selectedOrder.paymentStatus || 'PAID'}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping & Handling</span>
                  <span className="font-semibold text-emerald-500">Free Express</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 text-sm font-extrabold text-foreground">
                  <span>Total Paid</span>
                  <span className="font-display text-base text-primary">
                    {formatCurrency(selectedOrder.totalAmount)}
                  </span>
                </div>
              </div>

              {(selectedOrder.orderStatus === 'DELIVERED' || selectedOrder.status === 'DELIVERED') && (
                <div className="pt-2">
                  {getOrderReview(selectedOrder.id) ? (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 flex items-center justify-between">
                      <div className="space-y-0.5">
                        <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                          <CheckCircle2 className="size-3.5 text-emerald-500" />
                          Review Submitted
                        </span>
                        <p className="text-[11px] text-muted-foreground">
                          "{getOrderReview(selectedOrder.id)?.comment}"
                        </p>
                      </div>
                      <span className="text-xs font-bold text-amber-500 bg-background/80 px-2 py-1 rounded-md border border-amber-500/20">
                        {getOrderReview(selectedOrder.id)?.rating}★
                      </span>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs gap-1.5"
                      onClick={() => {
                        const o = selectedOrder
                        setSelectedOrder(null)
                        setReviewingOrder(o)
                        setRating(5)
                        setComment('')
                      }}
                    >
                      <Star className="size-3.5 fill-white" /> Write Review for Delivered Part
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Part Review Modal */}
      <Dialog open={Boolean(reviewingOrder)} onOpenChange={() => setReviewingOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
              <Star className="size-5 fill-amber-500 text-amber-500" />
              Rate & Review Delivered Part
            </DialogTitle>
          </DialogHeader>

          {reviewingOrder && (
            <div className="space-y-4 pt-2">
              <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Order #{reviewingOrder.orderNumber}
                </p>
                <p className="text-sm font-bold text-foreground">
                  {reviewingOrder.items?.[0]?.title || 'Vehicle Spare Part'}
                </p>
                {reviewingOrder.items?.length > 1 && (
                  <p className="text-xs text-muted-foreground">
                    + {reviewingOrder.items.length - 1} other item(s) in this order
                  </p>
                )}
              </div>

              {/* Star Rating Selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground">
                  Overall Rating <span className="text-destructive">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="p-1 transition-transform hover:scale-110 focus:outline-none"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      <Star
                        className={`size-7 transition-colors ${
                          (hoverRating || rating) >= star
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-muted-foreground/30'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="text-sm font-bold text-amber-500 ml-2">
                    {rating} / 5 Stars
                  </span>
                </div>
              </div>

              {/* Review Comment */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground">
                  Your Review & Fitment Experience <span className="text-destructive">*</span>
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share details about the part quality, exactness of fit, packaging condition, and delivery speed..."
                  className="min-h-28 text-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setReviewingOrder(null)}
                  disabled={submitPartReview.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-primary hover:bg-primary/90 text-white font-bold text-xs"
                  disabled={!comment.trim() || submitPartReview.isPending}
                  onClick={() => {
                    submitPartReview.mutate(
                      {
                        orderId: reviewingOrder.id,
                        partId: reviewingOrder.items?.[0]?.partId,
                        rating,
                        comment: comment.trim(),
                      },
                      {
                        onSuccess: () => {
                          toast.success('Thank you! Your verified part review has been submitted successfully.')
                          setReviewingOrder(null)
                        },
                        onError: (err: any) => {
                          toast.error(err?.message || 'Failed to submit review')
                        },
                      },
                    )
                  }}
                >
                  {submitPartReview.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin mr-2" /> Submitting...
                    </>
                  ) : (
                    'Submit Review'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bigship Live Courier Tracking Dialog */}
      <TrackingDialog
        open={Boolean(trackingOrder)}
        onOpenChange={() => setTrackingOrder(null)}
        awbNumber={trackingOrder?.awbNumber}
        orderNumber={trackingOrder?.orderNumber}
        orderId={trackingOrder?.id}
        isAdmin={false}
      />
    </div>
  )
}
